import { environment } from 'src/environments/environment';
import { readFileInventoryId } from 'src/app/interfaces/fileinventory';
import { DEFAULT_PAGE_SEO } from './seo.defaults';
import { PageSeo } from './seo.model';

/** Inventory shape used to pick a stable SEO image (banner / logo / thumbnail). */
export type SeoImageInventoryRef = {
  id?: string | number | null;
  url?: string | null;
} | null | undefined;

export function buildSeoImageProxyUrl(
  fileInventoryId: string,
  apiBaseUrl = environment.apiBaseUrl,
): string {
  const id = fileInventoryId.trim();
  const base = apiBaseUrl.replace(/\/$/, '');
  return `${base}/seo-image/file/${encodeURIComponent(id)}`;
}

/** First inventory with a usable id (banner before logo, thumbnail, etc.). */
export function pickSeoFileInventoryId(...inventories: SeoImageInventoryRef[]): string | undefined {
  for (const inventory of inventories) {
    const id = readFileInventoryId(inventory?.id);
    if (id) {
      return id;
    }
  }
  return undefined;
}

/** SAS / placeholder URLs that crawlers cannot keep using after the signature expires. */
export function isEphemeralSeoImageUrl(url: string | null | undefined): boolean {
  const raw = url?.trim() ?? '';
  if (!raw || raw === 'REDACTED' || raw === 'assets/images/wm.png') {
    return true;
  }
  if (/blob\.core\.windows\.net/i.test(raw) && /[?&](sig|se)=/i.test(raw)) {
    return true;
  }
  return false;
}

export function toAbsoluteSeoUrl(href: string | undefined, appOrigin: string): string {
  const raw = href ?? DEFAULT_PAGE_SEO.imageUrl ?? '/assets/images/main_logo_ln.png';
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return new URL(path, appOrigin.endsWith('/') ? appOrigin : `${appOrigin}/`).href;
}

/** Prefer the API proxy; never put a one-hour SAS into og:image. */
export function resolveSeoShareImageUrl(
  page: Pick<PageSeo, 'imageUrl' | 'imageFileInventoryId'>,
  appOrigin: string,
): string {
  const inventoryId = page.imageFileInventoryId?.trim();
  if (inventoryId) {
    return buildSeoImageProxyUrl(inventoryId);
  }
  if (isEphemeralSeoImageUrl(page.imageUrl)) {
    return toAbsoluteSeoUrl(DEFAULT_PAGE_SEO.imageUrl, appOrigin);
  }
  return toAbsoluteSeoUrl(page.imageUrl, appOrigin);
}
